import { useCallback, useEffect, useRef, useState } from "react"
import useApi from "./useApi.js"

const POLL_INTERVAL = 3000

export default function useProfile() {
  const api = useApi()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const pollRef = useRef(null)

  const fetchOnce = useCallback(async () => {
    try {
      const data = await api.get("/api/v1/shop/profile")
      setProfile(data)
      setError(null)
      return data
    } catch (err) {
      setError(err)
      return null
    } finally {
      setLoading(false)
    }
  }, [ api ])

  useEffect(() => {
    fetchOnce()
  }, [ fetchOnce ])

  // Poll while a re-analyze is in flight (crawl_status === "crawling").
  // Stops the moment the server flips status away from crawling.
  useEffect(() => {
    if (!profile) return
    if (profile.crawl_status !== "crawling") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
      return
    }
    if (pollRef.current) return
    pollRef.current = setInterval(fetchOnce, POLL_INTERVAL)
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [ profile?.crawl_status, fetchOnce ])

  const save = useCallback(async (patch) => {
    const data = await api.patch("/api/v1/shop/profile", { profile: patch })
    setProfile(data)
    return data
  }, [ api ])

  const revert = useCallback(async (path) => {
    const data = await api.post("/api/v1/shop/profile/revert", { path })
    setProfile(data)
    return data
  }, [ api ])

  const reAnalyze = useCallback(async () => {
    await api.post("/api/v1/shop/analyze")
    await fetchOnce()
  }, [ api, fetchOnce ])

  // M1f: screenshot + vision are now part of the main re-analyze pipeline
  // (no standalone retake endpoint). The `crawl_status === "crawling"` poller
  // above picks up the new screenshot + palette once the job finishes.
  return { profile, loading, error, refresh: fetchOnce, save, revert, reAnalyze }
}
